import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserPlus, Loader2, MessageSquare, Phone, X } from "lucide-react";

const STAGES = ["new", "contacted", "qualified", "won", "lost"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/leads").then(r => setLeads(r.data)).finally(() => setLoading(false)); }, []);

  const setStage = async (lead, stage) => {
    const r = await api.patch(`/leads/${lead.id}`, { stage });
    setLeads(prev => prev.map(l => l.id === lead.id ? r.data : l));
    toast.success(`Moved to ${stage}`);
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="leads-page">
      <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Pipeline</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">Leads</h1>
      <p className="text-zinc-400 mt-2 text-sm">Every customer who completed an AI chat. Captured details + stage tracking.</p>

      {loading ? <div className="mt-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin"/></div>
        : leads.length === 0 ? (
          <div className="mt-10 bg-zinc-950 border border-white/10 rounded-xl p-12 text-center">
            <UserPlus className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <div className="font-display text-lg">No leads yet</div>
            <div className="text-zinc-500 text-sm">Share your agent link — completed chats land here.</div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {STAGES.map(stage => (
              <div key={stage} className="bg-zinc-950 border border-white/10 rounded-xl p-4 min-h-[200px]" data-testid={`stage-${stage}`}>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3 flex items-center justify-between">
                  <span>{stage}</span>
                  <span className="text-zinc-700">{leads.filter(l => l.stage === stage).length}</span>
                </div>
                <div className="space-y-2">
                  {leads.filter(l => l.stage === stage).map(l => (
                    <button key={l.id} onClick={() => setOpen(l)} className="w-full text-left bg-black/40 border border-white/5 rounded-md p-3 hover:border-white/20 transition" data-testid={`lead-${l.id}`}>
                      <div className="text-sm font-medium truncate">{l.contact_name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono truncate">{l.contact_phone || "no phone"}</div>
                      <div className="text-[11px] text-zinc-600 mt-1">via {l.source}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      {open && <LeadDetail lead={open} onClose={() => setOpen(null)} onStage={setStage} onView={(sid) => { setOpen(null); nav(`/app/conversations/${sid}`); }} />}
    </div>
  );
}

function LeadDetail({ lead, onClose, onStage, onView }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="lead-detail">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Lead</div>
            <div className="font-display text-2xl tracking-tighter mt-1">{lead.contact_name}</div>
            <div className="text-xs text-zinc-500 font-mono mt-1 flex items-center gap-1.5"><Phone className="w-3 h-3"/> {lead.contact_phone || "—"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
        </div>

        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Captured by AI</div>
        <div className="bg-black/40 border border-white/5 rounded-lg p-3 space-y-1 max-h-64 overflow-y-auto">
          {Object.keys(lead.captured_data || {}).length === 0 && <div className="text-xs text-zinc-500">Nothing captured</div>}
          {Object.entries(lead.captured_data || {}).map(([k, v]) => (
            <div key={k} className="text-sm flex gap-2">
              <span className="text-zinc-500 text-xs uppercase tracking-wider min-w-[100px]">{k}</span>
              <span className="text-white">{String(v)}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Move stage</div>
        <div className="flex flex-wrap gap-1.5">
          {STAGES.map(s => (
            <button key={s} onClick={() => onStage(lead, s)} className={`text-xs px-2.5 py-1 rounded-full border transition ${lead.stage === s ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-zinc-400 border-white/10 hover:border-white/30 hover:text-white"}`} data-testid={`stage-btn-${s}`}>
              {s}
            </button>
          ))}
        </div>

        <Button onClick={() => onView(lead.session_id)} variant="outline" className="mt-5 w-full bg-transparent border-white/10 text-white hover:bg-white/10" data-testid="view-transcript-btn">
          <MessageSquare className="w-4 h-4 mr-2"/> View full transcript
        </Button>
      </div>
    </div>
  );
}
